import {
  OreButton,
  OreDivider,
  OreHeading,
  OreStack,
  OreTab,
  OreTabs,
  OreText,
  SvgDownload,
} from '@edenredespana/oreneta'
import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import {
  AutoEnrollmentDefaultConditionsModel,
  AutoEnrollmentDefaultConditionsSubProductModel,
} from '@/AutoEnrollment/domain'
import { Step3ServicesTable, Step3SubProductsTable } from '@/AutoEnrollment/ui/atoms'
import { createApiContractRepository } from '@/Contract/infrastructure'
import { contractTranslation } from '@/Contract/ui/contractTranslation'
import { useProductsDataController } from '@/Contract/ui/hooks'
import { ProductsContractingBanner } from '@/Contract/ui/molecules'
import { SITE_INFO } from '@/Shared/constants/siteInfo'
import { GetDateFormatted, HttpMethod, WebTagPermissionEnum } from '@/Shared/domain'
import { axiosFetch } from '@/Shared/infrastructure'
import { getAutoEnrollmentDefaultConditions } from '@/Shared/infrastructure/endpoints'
import { PageWrapper } from '@/Shared/ui/layout'
import { navigationRoutes } from '@/Shared/ui/routers'
import { useWebTagPermissions } from '@/User/context/WebTagPermissionsProvider'
import { useUserData } from '@/User/ui/context'

export const ContractProductsDataPage = (): JSX.Element => {
  const { t } = useTranslation()
  const { hasPermission } = useWebTagPermissions()
  const { userProducts, merchant } = useUserData()
  const navigation = useNavigate()
  const location = useLocation()
  const [selectedTab, setSelectedTab] = useState(
    (location.state as { selectedTab?: number })?.selectedTab ?? 0,
  )
  const [defaultConditions, setDefaultConditions] = useState<
    AutoEnrollmentDefaultConditionsModel[]
  >([])

  const { model, downloadContracts } = useProductsDataController(
    merchant.id,
    createApiContractRepository(),
    userProducts.map(p => p.productCode),
  )

  const isIndependent = merchant?.isIndependent
  const hasPremium = userProducts.some(product => product.productCode === 33)
  const selectedProductId = model?.[selectedTab]?.id
  const defaultConditionSubProducts: AutoEnrollmentDefaultConditionsSubProductModel[] =
    defaultConditions.find(product => product.productCode === selectedProductId)
      ?.subProducts ?? []
  const origin = navigationRoutes.contractsProductsData

  useEffect(() => {
    const getDefaultConditions = async (): Promise<void> => {
      try {
        const response = await axiosFetch(
          getAutoEnrollmentDefaultConditions,
          HttpMethod.GET,
        )
        setDefaultConditions(
          (response?.data ?? []) as AutoEnrollmentDefaultConditionsModel[],
        )
      } catch (error) {
        console.error(
          '[getAutoEnrollmentDefaultConditions][ContractProductsDataPage] error',
          error,
        )
      }
    }

    void getDefaultConditions()
  }, [])

  const handleButtonClick = (): void => {
    navigation(navigationRoutes.contractNewProducts, { state: { origin } })
  }
  return (
    <PageWrapper
      paddingSmaller
      paddingTop={8}
      style={{
        paddingRight: 40,
        paddingLeft: 40,
        minWidth: '-webkit-fill-available',
        marginRight: 0,
        marginLeft: 0,
      }}
    >
      <OreHeading as="h1" size="title-sm">
        {t(contractTranslation.productsData.pageTitle)}
      </OreHeading>
      <OreDivider space="medium" />
      <OreStack space="large" placeContent="stretch">
        {hasPermission(WebTagPermissionEnum.ACTION_CONTRACT_NEW_PRODUCTS) &&
          isIndependent &&
          !hasPremium && (
            <ProductsContractingBanner origin={navigationRoutes.contractsProductsData} />
          )}
        <OreStack space="xlarge" placeContent="stretch">
          {model && model.length > 0 ? (
            <OreTabs>
              {model.map((product, index) => (
                <OreTab
                  key={index}
                  label={
                    userProducts.find(p => p.productCode === product.id)?.productName ??
                    ''
                  }
                  active={selectedTab === index}
                  handleClick={() => setSelectedTab(index)}
                ></OreTab>
              ))}
            </OreTabs>
          ) : null}
          {selectedTab === 1 ? (
            <OreText tone="neutral-500">
              <Trans t={t}>{t(contractTranslation.productsData.premiumLegend)}</Trans>
            </OreText>
          ) : null}
          {model && model.length > 0 && model[selectedTab].subProducts?.length ? (
            <Step3SubProductsTable
              subProducts={model[selectedTab].subProducts}
              amendmentStartDate={model[selectedTab].amendmentStartDate}
              disabledState={false}
            />
          ) : null}
          {model && model.length > 0 && !model[selectedTab].subProducts?.length ? (
            <Step3ServicesTable
              services={[
                {
                  id: -1,
                  name: t(contractTranslation.productsData.products.table.state),
                  value:
                    t(contractTranslation.productsData.products.table.valid) +
                    ' ' +
                    GetDateFormatted(model[selectedTab].contractOpenDate),
                  family: '',
                },
                ...model[selectedTab].services,
              ]}
              disabledState={false}
            />
          ) : null}
        </OreStack>
        <div>
          {model &&
            model.length > 0 &&
            model[selectedTab]?.isContractDownloadable &&
            hasPermission(
              WebTagPermissionEnum.ACTION_DOWNLOAD_HIRED_PRODUCTS_CONTRACT,
            ) && (
              <OreStack direction="column" placeItems="start" space="medium">
                <OreButton
                  category="secondary"
                  onClick={() => downloadContracts(model[selectedTab])}
                  icon={<SvgDownload />}
                >
                  {t(contractTranslation.productsData.products.buttons.downloadContract)}
                </OreButton>
                <OreText size="text-sm" tone="neutral-500">
                  {t(
                    contractTranslation.productsData.products.buttons
                      .downloadContractHelpText,
                  )}
                </OreText>
              </OreStack>
            )}
        </div>
        {/* Segunda tabla: muestra los subproductos disponibles para contratar del producto activo */}
        {defaultConditionSubProducts.length ? (
          <div style={{ marginBottom: '25px' }}>
            <Step3SubProductsTable
              subProducts={defaultConditionSubProducts}
              disabledState={false}
              showServiceTooltip
              title={t(
                contractTranslation.productsData.products.availableSubProductsTitle,
              )}
            />
            {isIndependent && (
              <OreStack style={{ marginTop: '25px' }}>
                <OreButton
                  as="button"
                  category="primary"
                  onClick={handleButtonClick}
                  size="large"
                  data-testid="deleteAccount"
                >
                  {t(contractTranslation.productsData.products.buttons.hire)}
                </OreButton>
              </OreStack>
            )}
          </div>
        ) : null}
      </OreStack>
      <OreText tone="neutral-600" style={{ marginTop: '25px' }}>
        {t(contractTranslation.productsData.products.mailto)}
        <a href={`mailto: ${SITE_INFO.contact.email}`}> {SITE_INFO.contact.email}</a>
      </OreText>
    </PageWrapper>
  )
}
